import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridRowParams } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import { useUserDateFormatter } from '../../../../shared/hooks/useUserDateFormatter';
import { DIAM_GRID_PAGE_SIZE_OPTIONS } from '../../diamGridUtils';
import { useDiamGridPagination } from '../../hooks/useDiamGridPagination';
import { Confirm } from '../../../userManagement/components/Confirm';
import { DiamDataGrid } from '../DiamDataGrid/DiamDataGrid';
import { DataGridContainer } from '../DiamDataGrid/styles';
import { DiamDataGridTabs } from '../DiamDataGrid/types';
import { getRoleDetailsPath } from '../RoleDetails/constants';
import { mapApiRoleToTeamRole } from '../RoleDetails/mapApiRole';
import { ROLE_CATEGORIES } from '../RoleDetails/types';
import { buildTeamRoleColumns } from './columns';
import { TeamRole, TeamRoleActionHandlers } from './types';
import { useDeleteTeamRole } from './useDeleteTeamRole';
import { useTeamRoles } from './useTeamRoles';

interface TeamRolesGridProps extends TeamRoleActionHandlers {
  tabs?: DiamDataGridTabs;
}

export function TeamRolesGrid({ onEdit, onDelete, tabs }: TeamRolesGridProps) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { formatDateTime } = useUserDateFormatter();
  const {
    pageIndex,
    pageSize,
    paginationModel,
    filterModel,
    sortModel,
    handleServerFilterModelChange,
    handlePagination,
    handleSortModelChange,
  } = useDiamGridPagination({
    initialSortField: 'name',
    initialSortOrder: 'asc',
  });

  const teamRolesQuery = useTeamRoles({
    page: pageIndex,
    pageSize,
    sortModel,
    filterModel,
  });
  const deleteTeamRole = useDeleteTeamRole();
  const [rolePendingDeletion, setRolePendingDeletion] =
    useState<TeamRole | null>(null);

  const rows = useMemo(
    () =>
      (teamRolesQuery.data?.elements ?? []).map(role =>
        mapApiRoleToTeamRole(role, teamRolesQuery.data?.teamNameById),
      ),
    [teamRolesQuery.data],
  );

  useEffect(() => {
    if (teamRolesQuery.isError) {
      enqueueSnackbar(
        getApiErrorMessage(teamRolesQuery.error, 'Failed to load team roles'),
        { variant: 'error' },
      );
    }
  }, [enqueueSnackbar, teamRolesQuery.error, teamRolesQuery.isError]);

  const handleRequestDelete = useCallback((teamRole: TeamRole) => {
    setRolePendingDeletion(teamRole);
  }, []);

  const handleCloseDelete = () => {
    setRolePendingDeletion(null);
  };

  const handleConfirmDelete = async () => {
    if (!rolePendingDeletion) return;

    try {
      await deleteTeamRole.mutateAsync(rolePendingDeletion.id);
      onDelete?.(rolePendingDeletion);
      enqueueSnackbar(`Deleted ${rolePendingDeletion.name}`, {
        variant: 'success',
      });
      setRolePendingDeletion(null);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Failed to delete role'), {
        variant: 'error',
      });
    }
  };

  const columns = useMemo(
    () =>
      buildTeamRoleColumns({
        onEdit,
        onDelete: handleRequestDelete,
        formatDateTime,
      }),
    [formatDateTime, handleRequestDelete, onEdit],
  );

  function handleRowClick(params: GridRowParams<TeamRole>) {
    navigate(getRoleDetailsPath(ROLE_CATEGORIES.custom, params.row.id));
  }

  return (
    <DataGridContainer elevation={1}>
      <DiamDataGrid
        rows={rows}
        columns={columns}
        loading={teamRolesQuery.isPending || teamRolesQuery.isFetching}
        disableRowSelectionOnClick
        getRowClassName={() => 'clickable'}
        onRowClick={handleRowClick}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={handleServerFilterModelChange}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePagination}
        rowCount={teamRolesQuery.data?.total ?? 0}
        pageSizeOptions={[...DIAM_GRID_PAGE_SIZE_OPTIONS]}
        searchPlaceholder="Search roles"
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        tabs={tabs}
      />
      <Confirm
        isOpen={Boolean(rolePendingDeletion)}
        title="Delete role"
        content="You are going to delete the role. Users with the role assigned will be affected. Are you sure you want to proceed?"
        confirm="Yes"
        cancel="No"
        onConfirm={handleConfirmDelete}
        onClose={handleCloseDelete}
      />
    </DataGridContainer>
  );
}
